import { useState, useRef, useCallback, useEffect } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import axios from 'axios'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Textarea } from '../components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import { Checkbox } from '../components/ui/checkbox'
import {
  Loader2,
  MapPin,
  Image,
  Upload,
  X,
  Palette,
  Lock,
  Globe,
  Share2,
  Percent,
  CreditCard,
  Check,
  Users,
  Gift,
  Plus,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Eye,
} from 'lucide-react'
import { toast } from 'sonner'
import { useLanguage } from '../contexts/LanguageContext'
import LanguageToggle from '../components/LanguageToggle'
import ThemeMenu from '../components/ThemeMenu'
import ProfileButton from '../components/ProfileButton'
import AppFooter from '../components/AppFooter'
import BrandLogo from '../components/BrandLogo'
import DashboardButton from '../components/DashboardButton'
import DateRangePicker from '../components/DateRangePicker'
import DatePicker from '../components/DatePicker'
import TimePicker from '../components/TimePicker'
import LocationPickerMap from '../components/LocationPickerMap'
import AiDesignStudio from '../components/AiDesignStudio'
import InvitationLivePreview from '../components/InvitationLivePreview'
import InviteTemplatePicker from '../components/InviteTemplatePicker'
import InvitePreviewFrame from '../components/InvitePreviewFrame'
import CelebrationOverlay from '../components/CelebrationOverlay'
import { burstConfetti } from '../components/celebration'
import {
  getApiUrl,
  resolveImageUrl,
  currencySymbol,
  formatMoney,
  GIFT_CURRENCIES,
} from '../lib/utils'
import { WISHLIST_ICONS, wishlistEmoji } from '../lib/wishlistIcons'
import WishlistImageUpload from '../components/WishlistImageUpload'
import { loadGoogleMaps, getGoogleMapsApiKey } from '../lib/googleMaps'
import { cleanTemplateId, getTemplate } from '../lib/inviteTemplates'
import {
  EVENT_TYPES,
  eventTypeWantsGifts,
  localizeEventType,
  titleExampleFor,
} from '../lib/eventTypes'
import { ANIMATION_STYLES } from '../lib/animations'

const API = getApiUrl()

// `style` adds a browsable descriptor. Every theme is available to every host —
// creating an event is free, so there is nothing left to gate them behind.
const THEMES = [
  {
    id: 'classic',
    name: { en: 'Classic Elegant', ka: 'კლასიკური ელეგანტური' },
    style: { en: 'Elegant', ka: 'ელეგანტური' },
    color: 'bg-slate-800',
    preview: 'from-slate-800 to-slate-900',
  },
  {
    id: 'modern',
    name: { en: 'Modern Minimal', ka: 'თანამედროვე მინიმალისტური' },
    style: { en: 'Minimal', ka: 'მინიმალისტური' },
    color: 'bg-gray-900',
    preview: 'from-gray-900 to-black',
  },
  {
    id: 'kids',
    name: { en: 'Kids Celebration', ka: 'საბავშვო ზეიმი' },
    style: { en: 'Playful', ka: 'მხიარული' },
    color: 'bg-yellow-400',
    preview: 'from-yellow-400 via-orange-400 to-pink-500',
  },
  {
    id: 'romantic',
    name: { en: 'Romantic Floral', ka: 'რომანტიული ყვავილოვანი' },
    style: { en: 'Romantic', ka: 'რომანტიული' },
    color: 'bg-rose-400',
    preview: 'from-rose-400 to-pink-500',
  },
  {
    id: 'dark',
    name: { en: 'Dark Luxury', ka: 'მუქი ლუქსი' },
    style: { en: 'Luxury', ka: 'ლუქსი' },
    color: 'bg-zinc-900',
    preview: 'from-zinc-900 to-black',
  },
]

/** A #rrggbb colour from the query string, or '' when absent or malformed. */
const hexParam = (name: string) => {
  const v = (new URLSearchParams(window.location.search).get(name) || '').replace(/^#/, '')
  return /^[0-9a-f]{6}$/i.test(v) ? `#${v.toLowerCase()}` : ''
}

const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const isEventDateTimeInPast = (date, time) => {
  if (!date) return false
  const eventDateTime = new Date(`${date}T${time || '00:00'}`)
  return eventDateTime < new Date()
}

const buildGoogleMapsEmbedUrl = (location, latitude, longitude) => {
  const key = getGoogleMapsApiKey()

  let query
  if (latitude != null && longitude != null) {
    query = `${latitude},${longitude}`
  } else if (location?.trim()) {
    query = location.trim()
  } else {
    query = 'Tbilisi, Georgia'
  }

  // Official Maps Embed API ("place" mode) reliably drops a marker pin at the
  // location. The legacy `maps?q=...&output=embed` URL no longer shows a pin.
  if (key) {
    return `https://www.google.com/maps/embed/v1/place?key=${key}&q=${encodeURIComponent(
      query,
    )}&zoom=15`
  }
  // Fallback when no API key is configured.
  return `https://www.google.com/maps?q=${encodeURIComponent(query)}&z=15&output=embed`
}

// Wizard step order. Edit mode drops the trailing 'gifts' step (wishlist and
// billing only apply when creating). Kept at module scope so draft restore can
// reason about step positions before the labelled `steps` array is built.
const STEP_IDS = ['about', 'when', 'design', 'options', 'gifts'] as const

// Temporarily hidden on the Design step: the AI design studio and the
// theme/animation module. Flip back to true to restore them — the step then
// shows only the cover photo, language and colour pickers.
const SHOW_AI_DESIGN_TOOLS = false

const CreateEvent = () => {
  const navigate = useNavigate()
  const { eventId } = useParams()
  const isEdit = !!eventId
  // localStorage key for the in-progress event draft (create mode). Keeps the
  // form populated across reloads and declined/cancelled payments.
  const EVENT_DRAFT_KEY = 'storyfy_event_draft'
  const { language: appLang, t } = useLanguage()
  const today = getLocalDateString()

  // Restore an in-progress draft once (create mode only) so a reload or a
  // declined payment never wipes the form. Read synchronously here — and used
  // in the state initializers below — to avoid an effect-ordering race that
  // could clobber the draft on mount. Stale (now-past) date fields are dropped
  // so the form never reopens pre-filled with a date that's already in the past.
  const loadEventDraft = () => {
    if (isEdit) return null
    try {
      const raw = localStorage.getItem(EVENT_DRAFT_KEY)
      if (!raw) return null
      const draft = JSON.parse(raw)
      const fd = draft?.formData
      if (fd) {
        if (fd.date && fd.date < today) {
          fd.date = ''
          fd.time = ''
          fd.end_date = ''
          fd.end_time = ''
        }
        if (fd.rsvp_deadline && fd.rsvp_deadline < today) fd.rsvp_deadline = ''
        // Drop the removed organizer_name field from drafts saved by older builds.
        delete fd.organizer_name
      }
      return draft
    } catch {
      return null
    }
  }
  const initialDraftRef = useRef<any>(null)
  if (initialDraftRef.current === null) initialDraftRef.current = loadEventDraft() || {}
  const initialDraft = initialDraftRef.current

  // Restore the step the host was on, not just their answers — a reload should
  // put them back where they left off. Clamped to the furthest step whose
  // prerequisites actually hold: loadEventDraft strips now-past dates, so a
  // draft saved on a later step can come back with a required field empty, and
  // landing there would strand the host behind a gate they cannot see. Mirrors
  // validateStep's required fields, but stays silent — restoring is not a
  // failed navigation, so it must not fire that function's error toasts.
  const restoreStep = (draft: any): number => {
    if (isEdit) return 0
    const saved = Number(draft?.step)
    if (!Number.isInteger(saved) || saved <= 0) return 0
    const fd = draft?.formData || {}
    if (!fd.title || !fd.event_type) return 0
    if (!fd.date || !fd.time || !fd.location) return 1
    return Math.min(saved, STEP_IDS.length - 1)
  }

  const [loadingEvent, setLoadingEvent] = useState<boolean>(!!eventId)
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef(null)
  const locationInputRef = useRef(null)
  const autocompleteRef = useRef(null)
  const [dragActive, setDragActive] = useState(false)

  // Multi-step wizard: index into the `steps` array built below.
  const [step, setStep] = useState(() => restoreStep(initialDraft))

  // Billing. Creating an event is free — the only thing to show the host is the
  // commission we take on gifts, which billingConfig carries.
  const [billingConfig, setBillingConfig] = useState(null)
  const [createdEventId, setCreatedEventId] = useState(null)
  const [showCelebration, setShowCelebration] = useState(false)
  // "For myself" vs "For someone else" — when "other", we capture the honoree.
  const [eventFor, setEventFor] = useState(initialDraft?.eventFor || 'self')

  // Wishlist items added inline during creation (persisted after the event is
  // created, via the same /events/{id}/wishlist endpoint EventDetail uses).
  const [wishlistItems, setWishlistItems] = useState<any[]>(initialDraft?.wishlistItems || [])
  const [wishlistDraft, setWishlistDraft] = useState({
    title: '',
    target_amount: '',
    currency: 'GEL',
    icon: 'gift',
    image_url: '',
  })

  const [previewDevice, setPreviewDevice] = useState<'phone' | 'desktop'>('phone')

  const [formData, setFormData] = useState<any>({
    title: '',
    event_type: '',
    date: '',
    time: '',
    end_date: '',
    end_time: '',
    location: '',
    latitude: null,
    longitude: null,
    description: '',
    cover_image: '',
    theme: 'classic',
    animation_style: 'auto',
    // The gallery's "Use this template" lands here with ?template=<id>.
    invite_template: cleanTemplateId(new URLSearchParams(window.location.search).get('template')),
    language: 'both',
    rsvp_deadline: '',
    // RSVP closing controls, each independent of the deadline and of each other.
    rsvp_closed_manual: false,
    rsvp_invite_only: false,
    guest_capacity: '',       // blank = no limit; sent as 0 to clear one
    allow_guest_sharing: false,
    allow_plus_one: true,
    honoree_name: '',
    arrival_instructions: '',
    rsvp_label_maybe: '',
    rsvp_label_decline: '',
    dress_code: '',
    // English variants shown for bilingual ("both") events — the base fields
    // then hold the Georgian text.
    title_en: '',
    description_en: '',
    honoree_name_en: '',
    arrival_instructions_en: '',
    rsvp_label_maybe_en: '',
    rsvp_label_decline_en: '',
    dress_code_en: '',
    // Day schedule for multi-step events: rows of
    // {start, end, title, title_en, location, location_en}.
    itinerary: [],
    // Design overrides on top of invite_template. Empty = keep the template's
    // own colour / paper / type (InviteTemplatePicker seeds the pickers from
    // the template so "empty" still shows as a concrete selected value).
    // The gallery's quick preview can pass the colours picked there too.
    accent_color: hexParam('accent'),
    background_color: hexParam('bg'),
    invite_font: '',
    font_heading: '',
    font_body: '',
    show_gifts: true,
    ...(initialDraft?.formData || {}),
  })

  // Whether the host has taken the gifts toggle into their own hands. Until
  // they do, picking an event type sets it: a corporate gala or a fundraiser
  // opens with gifts off, a birthday with them on. Once they flip the switch
  // themselves their choice sticks, whatever type they pick afterwards.
  // Edit mode starts locked — the stored value already is the host's answer,
  // and changing the type must not silently unpublish a live gift list.
  const [giftsTouched, setGiftsTouched] = useState(isEdit)

  const handleEventTypeSelect = (value: string) =>
    setFormData((prev: any) => ({
      ...prev,
      event_type: value,
      ...(giftsTouched ? {} : { show_gifts: eventTypeWantsGifts(value) }),
    }))

  const emptyItineraryRow = () => ({
    start: '',
    end: '',
    title: '',
    title_en: '',
    location: '',
    location_en: '',
  })
  const addItineraryRow = () =>
    setFormData((prev: any) => ({
      ...prev,
      itinerary: [...(prev.itinerary || []), emptyItineraryRow()],
    }))
  const updateItineraryRow = (i: number, patch: any) =>
    setFormData((prev: any) => ({
      ...prev,
      itinerary: prev.itinerary.map((r: any, j: number) => (j === i ? { ...r, ...patch } : r)),
    }))
  const removeItineraryRow = (i: number) =>
    setFormData((prev: any) => ({
      ...prev,
      itinerary: prev.itinerary.filter((_: any, j: number) => j !== i),
    }))

  const mapPreviewUrl = buildGoogleMapsEmbedUrl(
    formData.location,
    formData.latitude,
    formData.longitude,
  )

  // Load the commission rate we quote to hosts.
  useEffect(() => {
    axios
      .get(`${API}/billing/config`, { withCredentials: true })
      .then((res) => setBillingConfig(res.data))
      .catch(() => {
        // Billing config is non-critical for rendering the form
      })
  }, [])

  // Edit mode: load the existing event and prefill the form.
  useEffect(() => {
    if (!eventId) return
    let active = true
    setLoadingEvent(true)
    axios
      .get(`${API}/events/${eventId}`, { withCredentials: true })
      .then((res) => {
        if (!active) return
        const e = res.data
        setFormData((prev: any) => ({
          ...prev,
          title: e.title || '',
          event_type: e.event_type || '',
          date: e.date || '',
          time: e.time || '',
          end_date: e.end_date || '',
          end_time: e.end_time || '',
          location: e.location || '',
          latitude: e.latitude ?? null,
          longitude: e.longitude ?? null,
          description: e.description || '',
          cover_image: e.cover_image || '',
          theme: e.theme || 'classic',
          animation_style: e.animation_style || 'auto',
          invite_template: e.invite_template || 'auto',
          language: e.language || 'en',
          rsvp_deadline: e.rsvp_deadline || '',
          rsvp_closed_manual: !!e.rsvp_closed_manual,
          rsvp_invite_only: !!e.rsvp_invite_only,
          guest_capacity: e.guest_capacity ? String(e.guest_capacity) : '',
          allow_guest_sharing: !!e.allow_guest_sharing,
          allow_plus_one: e.allow_plus_one !== false,
          honoree_name: e.honoree_name || '',
          arrival_instructions: e.arrival_instructions || '',
          rsvp_label_maybe: e.rsvp_label_maybe || '',
          rsvp_label_decline: e.rsvp_label_decline || '',
          dress_code: e.dress_code || '',
          title_en: e.title_en || '',
          description_en: e.description_en || '',
          honoree_name_en: e.honoree_name_en || '',
          arrival_instructions_en: e.arrival_instructions_en || '',
          rsvp_label_maybe_en: e.rsvp_label_maybe_en || '',
          rsvp_label_decline_en: e.rsvp_label_decline_en || '',
          dress_code_en: e.dress_code_en || '',
          itinerary: Array.isArray(e.itinerary)
            ? e.itinerary.map((r: any) => ({
                start: r.start || '',
                end: r.end || '',
                title: r.title || '',
                title_en: r.title_en || '',
                location: r.location || '',
                location_en: r.location_en || '',
              }))
            : [],
          accent_color: e.accent_color || '',
          background_color: e.background_color || '',
          invite_font: e.invite_font || '',
          font_heading: e.font_heading || '',
          font_body: e.font_body || '',
          show_gifts: e.show_gifts !== false,
        }))
        setEventFor(e.honoree_name ? 'other' : 'self')
      })
      .catch(() => {
        toast.error(
          appLang === 'ka' ? 'ღონისძიება ვერ ჩაიტვირთა' : 'Could not load the event',
        )
        navigate('/dashboard')
      })
      .finally(() => {
        if (active) setLoadingEvent(false)
      })
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId])

  // (Draft restore happens synchronously in the state initializers above, via
  // loadEventDraft() — avoids an effect race that clobbered the draft on mount.)

  // Persist the draft whenever meaningful inputs change (create mode only).
  useEffect(() => {
    if (isEdit) return
    try {
      localStorage.setItem(
        EVENT_DRAFT_KEY,
        JSON.stringify({ formData, wishlistItems, eventFor, step }),
      )
    } catch {
      /* storage unavailable — non-critical */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, formData, wishlistItems, eventFor, step])

  // Bind Google Places autocomplete to the location input. The input only
  // mounts on the "When & where" step, so we re-run whenever `step` changes to
  // (re)attach the listener once the field is in the DOM.
  useEffect(() => {
    const apiKey = getGoogleMapsApiKey()
    let isActive = true

    const setupAutocomplete = async () => {
      if (!apiKey || !locationInputRef.current) {
        return
      }

      try {
        await loadGoogleMaps(apiKey)
      } catch {
        return
      }

      if (
        !isActive ||
        !locationInputRef.current ||
        !window.google?.maps?.places
      ) {
        return
      }

      autocompleteRef.current?.unbindAll?.()
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        locationInputRef.current,
        {
          fields: ['formatted_address', 'geometry', 'name'],
        },
      )

      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current?.getPlace?.()

        if (!place) {
          return
        }

        // Keep the venue name in the stored location — formatted_address alone
        // is often just the street address (e.g. drops "კაკლები რესტორანი").
        const venueName = place.name?.trim()
        const formattedLocation =
          venueName &&
          place.formatted_address &&
          !place.formatted_address.toLowerCase().includes(venueName.toLowerCase())
            ? `${venueName}, ${place.formatted_address}`
            : place.formatted_address ||
              place.name ||
              locationInputRef.current.value
        const latitude = place.geometry?.location?.lat?.() ?? null
        const longitude = place.geometry?.location?.lng?.() ?? null

        setFormData((prevFormData) => ({
          ...prevFormData,
          location: formattedLocation,
          latitude,
          longitude,
        }))
      })
    }

    setupAutocomplete()

    return () => {
      isActive = false
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners?.(
          autocompleteRef.current,
        )
        autocompleteRef.current = null
      }
    }
  }, [step])

  const handleImageUpload = useCallback(async (file) => {
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file')
      return
    }

    setUploading(true)
    const formDataUpload = new FormData()
    formDataUpload.append('file', file)

    try {
      const response = await axios.post(`${API}/upload/image`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setFormData((prevFormData) => ({
        ...prevFormData,
        cover_image: response.data.url,
      }))
      toast.success('Image uploaded!')
    } catch (error) {
      toast.error('Failed to upload image')
    } finally {
      setUploading(false)
    }
  }, [])

  const handleDrag = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      setDragActive(false)
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        handleImageUpload(e.dataTransfer.files[0])
      }
    },
    [handleImageUpload],
  )

  // The Next and Create buttons swap in place between renders, so the second
  // click of a double-click (or a key-repeat Enter) lands on the next step's
  // button — skipping a step, or worse, submitting from the step before the
  // last one without the final step ever being shown. Swallow any forward
  // navigation or submit that fires right after a step change.
  const stepChangedAtRef = useRef(0)
  useEffect(() => {
    stepChangedAtRef.current = Date.now()
  }, [step])
  const stepJustChanged = () => Date.now() - stepChangedAtRef.current < 400

  if (loadingEvent) {
    return (
      <div className="min-h-screen bg-celebrate flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#ff4d8d]" />
      </div>
    )
  }

  const addWishlistDraft = () => {
    const amount = parseFloat(wishlistDraft.target_amount)
    if (!wishlistDraft.title.trim() || !wishlistDraft.target_amount) {
      toast.error(
        appLang === 'ka'
          ? 'სახელი და თანხა სავალდებულოა'
          : 'Item title and target amount are required',
      )
      return
    }
    if (!(amount > 0)) {
      toast.error(
        appLang === 'ka'
          ? 'თანხა უნდა იყოს ნულზე მეტი'
          : 'Target amount must be greater than zero',
      )
      return
    }
    setWishlistItems((prev) => [
      ...prev,
      {
        title: wishlistDraft.title.trim(),
        target_amount: amount,
        currency: wishlistDraft.currency,
        icon: wishlistDraft.icon,
        image_url: wishlistDraft.image_url || '',
      },
    ])
    // Keep the chosen currency for the next item, reset the rest.
    setWishlistDraft((prev) => ({
      title: '',
      target_amount: '',
      currency: prev.currency,
      icon: 'gift',
      image_url: '',
    }))
  }

  const removeWishlistItem = (index) => {
    setWishlistItems((prev) => prev.filter((_, i) => i !== index))
  }

  // Wizard steps. Wishlist + billing only apply when creating a new event, so
  // edit mode drops the final step. `step` indexes into this array.
  const STEP_LABELS: Record<(typeof STEP_IDS)[number], string> = {
    about: appLang === 'ka' ? 'შესახებ' : 'About',
    when: appLang === 'ka' ? 'როდის და სად' : 'When & where',
    design: appLang === 'ka' ? 'დიზაინი' : 'Design',
    options: appLang === 'ka' ? 'სტუმრები' : 'Guests',
    gifts: appLang === 'ka' ? 'საჩუქრები' : 'Gifts',
  }
  const steps = (isEdit ? STEP_IDS.filter((id) => id !== 'gifts') : STEP_IDS).map((id) => ({
    id,
    label: STEP_LABELS[id],
  }))
  const currentStep = steps[step]
  // The Design step lays the designer and a sticky live preview side by side,
  // so the page takes the whole screen for it — the preview is a real
  // invitation and only reads at size.
  const wideStep = currentStep?.id === 'design'
  const pageWidth = wideStep ? 'max-w-3xl lg:max-w-6xl xl:max-w-[96rem] 2xl:max-w-[110rem]' : 'max-w-3xl'
  // A phone preview is phone-sized whatever the room; the desktop one is a
  // whole invitation scaled down, so it takes every pixel the screen spares.
  const previewColumn =
    previewDevice === 'desktop'
      ? 'xl:grid-cols-[minmax(0,1fr)_clamp(420px,44vw,1000px)]'
      : 'xl:grid-cols-[minmax(0,1fr)_400px]'
  const designTemplate = getTemplate(cleanTemplateId(formData.invite_template))
  // A bilingual invitation has no single language of its own — guests pick —
  // so its preview follows the language the host is reading the app in. A
  // single-language event previews in the language its guests will get,
  // whatever the app is set to.
  const invLang = formData.language === 'both' ? appLang : formData.language
  // Mirrors InvitationPage's own bilingual pick: the English field when the
  // preview is in English and the host has filled it, the base field else.
  const inv = (base, en) =>
    (formData.language === 'both' && invLang === 'en' && (en || '').trim()) || base
  const previewWords = {
    title: inv(formData.title, formData.title_en),
    honoree: inv(formData.honoree_name, formData.honoree_name_en),
    date: formData.date,
    time: formData.time,
    location: formData.location,
  }
  const isLastStep = step === steps.length - 1

  // Per-step validation gate. Returns true when the step's fields are valid;
  // otherwise shows a toast and returns false so navigation is blocked. The
  // final submit re-runs the full set of checks as a backstop.
  const validateStep = (stepId) => {
    const fail = (en, ka) => {
      toast.error(appLang === 'ka' ? ka : en)
      return false
    }
    if (stepId === 'about') {
      if (!formData.title || !formData.event_type) {
        return fail(
          'Please fill in the event title and type',
          'შეავსე ღონისძიების სათაური და ტიპი',
        )
      }
    }
    if (stepId === 'when') {
      if (!formData.date || !formData.time || !formData.location) {
        return fail(
          'Please fill in the date, start time and location',
          'შეავსე თარიღი, დაწყების დრო და მდებარეობა',
        )
      }
      if (!isEdit && formData.date < today) {
        return fail(
          'Event date cannot be in the past',
          'ღონისძიების თარიღი ვერ იქნება წარსულში',
        )
      }
      if (!isEdit && isEventDateTimeInPast(formData.date, formData.time)) {
        return fail(
          'Event time cannot be in the past',
          'ღონისძიების დრო ვერ იქნება წარსულში',
        )
      }
      if (!isEdit && formData.rsvp_deadline && formData.rsvp_deadline < today) {
        return fail(
          'RSVP deadline cannot be in the past',
          'RSVP-ის ვადა ვერ იქნება წარსულში',
        )
      }
      if (formData.rsvp_deadline && formData.rsvp_deadline > formData.date) {
        return fail(
          'RSVP deadline cannot be after the event date',
          'RSVP-ის ვადა ვერ აღემატება ღონისძიების თარიღს',
        )
      }
      if (formData.end_date || formData.end_time) {
        const endDateEff = formData.end_date || formData.date
        const endTimeEff = formData.end_time || formData.time
        const startDT = new Date(`${formData.date}T${formData.time || '00:00'}`)
        const endDT = new Date(`${endDateEff}T${endTimeEff || '00:00'}`)
        if (endDT < startDT) {
          return fail(
            'The event end cannot be before its start',
            'დასრულება ვერ იქნება დაწყებამდე',
          )
        }
      }
    }
    return true
  }

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleNext = () => {
    if (stepJustChanged()) return
    if (!validateStep(currentStep.id)) return
    setStep((s) => Math.min(s + 1, steps.length - 1))
    scrollToTop()
  }

  const handleBack = () => {
    setStep((s) => Math.max(s - 1, 0))
    scrollToTop()
  }

  // Editing an event is not filling a form in order, so every step is one tap
  // away. Going forward still validates every step being skipped and stops on
  // the first one that fails, so an edit can never step around a required
  // field it just emptied.
  const goToStep = (i) => {
    if (i === step) return
    for (let j = step; j < i; j += 1) {
      if (!validateStep(steps[j].id)) {
        setStep(j)
        scrollToTop()
        return
      }
    }
    setStep(i)
    scrollToTop()
  }

  // Form-level submit handler. On any step but the last this just advances
  // (so pressing Enter moves forward); only the final step creates/saves.
  const handleSubmit = (e) => {
    e.preventDefault()
    if (!isLastStep) {
      handleNext()
      return
    }
    if (stepJustChanged()) return
    submitEvent()
  }

  const submitEvent = async ({ stay = false } = {}) => {
    if (
      !formData.title ||
      !formData.event_type ||
      !formData.date ||
      !formData.time ||
      !formData.location
    ) {
      toast.error(
        appLang === 'ka'
          ? 'შეავსე ყველა სავალდებულო ველი'
          : 'Please fill in all required fields',
      )
      return
    }

    if (!isEdit && formData.date < today) {
      toast.error(
        appLang === 'ka'
          ? 'ღონისძიების თარიღი ვერ იქნება წარსულში'
          : 'Event date cannot be in the past',
      )
      return
    }

    if (!isEdit && isEventDateTimeInPast(formData.date, formData.time)) {
      toast.error(
        appLang === 'ka'
          ? 'ღონისძიების დრო ვერ იქნება წარსულში'
          : 'Event time cannot be in the past',
      )
      return
    }

    if (!isEdit && formData.rsvp_deadline && formData.rsvp_deadline < today) {
      toast.error(
        appLang === 'ka'
          ? 'RSVP-ის ვადა ვერ იქნება წარსულში'
          : 'RSVP deadline cannot be in the past',
      )
      return
    }

    if (formData.rsvp_deadline && formData.rsvp_deadline > formData.date) {
      toast.error(
        appLang === 'ka'
          ? 'RSVP-ის ვადა ვერ აღემატება ღონისძიების თარიღს'
          : 'RSVP deadline cannot be after the event date',
      )
      return
    }

    // End (date/time) must not be before the start.
    if (formData.end_date || formData.end_time) {
      const endDateEff = formData.end_date || formData.date
      const endTimeEff = formData.end_time || formData.time
      const startDT = new Date(`${formData.date}T${formData.time || '00:00'}`)
      const endDT = new Date(`${endDateEff}T${endTimeEff || '00:00'}`)
      if (endDT < startDT) {
        toast.error(
          appLang === 'ka'
            ? 'დასრულება ვერ იქნება დაწყებამდე'
            : 'The event end cannot be before its start',
        )
        return
      }
    }

    setLoading(true)
    try {
      // Drop half-filled schedule rows (a step needs at least a start time and
      // a title to mean anything on the invitation).
      const body = {
        ...formData,
        itinerary: (formData.itinerary || []).filter(
          (r: any) => (r.title || '').trim() && r.start,
        ),
        // The field is a text input so it can be left blank; the API wants a
        // number or null, and null is what clears an existing limit on edit.
        guest_capacity: Number(formData.guest_capacity) || null,
        // A painted design shows no photo anywhere, so one is never saved with
        // it — the form keeps it, so switching back to a classic design brings
        // the host's photo back.
        cover_image: designTemplate.painted ? '' : formData.cover_image,
      }
      if (isEdit) {
        await axios.put(`${API}/events/${eventId}`, body, { withCredentials: true })
        toast.success(
          appLang === 'ka' ? 'ღონისძიება განახლდა' : 'Event updated',
        )
        // Saved from the step header: the host is mid-edit, so stay where they are.
        if (!stay) navigate(`/events/${eventId}`)
        return
      }
      const response = await axios.post(`${API}/events`, body, {
        withCredentials: true,
      })
      const newEventId = response.data.event_id
      // The event now exists on the server — the local draft is no longer needed.
      try {
        localStorage.removeItem(EVENT_DRAFT_KEY)
      } catch {
        /* ignore */
      }

      // Persist any wishlist items added during creation. Posted sequentially
      // so they keep the order the host added them. The event already exists,
      // so a wishlist failure shouldn't block the flow — the host can finish
      // the list from the event page.
      if (newEventId && wishlistItems.length > 0) {
        try {
          for (const item of wishlistItems) {
            await axios.post(
              `${API}/events/${newEventId}/wishlist`,
              {
                title: item.title,
                target_amount: item.target_amount,
                currency: item.currency,
                icon: item.icon,
                image_url: item.image_url || '',
              },
              { withCredentials: true },
            )
          }
        } catch (wishlistError) {
          toast.error(
            appLang === 'ka'
              ? 'ზოგი სასურველი ნივთი ვერ შეინახა — დაამატე ღონისძიების გვერდიდან'
              : 'Some wishlist items could not be saved — you can add them from the event page',
          )
        }
      }

      // Celebrate the publish moment (no-op under prefers-reduced-motion).
      burstConfetti()
      toast.success(
        appLang === 'ka'
          ? 'ღონისძიება შეიქმნა!'
          : 'Event created successfully!',
      )
      navigate(`/events/${response.data.event_id}`)
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event')
    } finally {
      setLoading(false)
    }
  }

  const goToCreatedEvent = () => {
    navigate(`/events/${createdEventId}`)
  }

  const selectedTheme =
    THEMES.find((th) => th.id === formData.theme) || THEMES[0]

  const handleThemeSelect = (id) => {
    setFormData({ ...formData, theme: id })
  }

  return (
    <div className="min-h-screen bg-celebrate flex flex-col">
      {/* Header */}
      <header className="bg-[#fff7f2]/80 dark:bg-[#1e1438]/80 backdrop-blur-xl border-b border-[#1b1033]/8 dark:border-white/10 sticky top-0 z-50">
        <div className={`${pageWidth} mx-auto px-4 sm:px-6 lg:px-8`}>
          <div className="flex items-center h-16 gap-3">
            <BrandLogo />
            <DashboardButton />
            <div className="ml-auto flex items-center gap-2 sm:gap-4">
              <LanguageToggle />
              <ThemeMenu />
              <ProfileButton />
            </div>
          </div>
        </div>
      </header>

      <main className={`flex-1 min-h-[calc(100dvh-4rem)] ${pageWidth} mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 sm:py-10`}>
        <div className="mb-7 animate-fade-in">
          <h1 className="font-display font-bold text-[2.5rem] md:text-[3rem] leading-none tracking-tight text-[#1b1033] dark:text-[#f4f0ff]">
            {isEdit ? t.editEvent : t.createEventTitle}
          </h1>
          <p className="text-[#4a3d66] dark:text-gray-400 mt-3 text-lg">
            {isEdit
              ? appLang === 'ka'
                ? 'შეცვალე ღონისძიების დეტალები'
                : 'Update your event details'
              : appLang === 'ka'
                ? 'შეავსე დეტალები და დაიწყე სტუმრების მოწვევა'
                : 'Fill in the details and start inviting guests'}
          </p>
        </div>
        <div className="glass-card rounded-bubble shadow-celebrate-sm p-6 md:p-8 animate-fade-in">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Step progress. While editing it stays in view with a Save
                button, so a change on any step can be kept without walking to
                the end of the wizard. */}
            <div
              className={
                isEdit
                  ? 'sticky top-16 z-30 -mx-6 bg-[var(--surface)] px-6 pb-3 pt-2 md:-mx-8 md:px-8'
                  : undefined
              }
            >
              <div className="flex items-center justify-between gap-3 mb-2.5">
                <span className="truncate text-sm font-semibold text-[#1b1033] dark:text-[#f4f0ff]">
                  {currentStep.label}
                </span>
                <div className="flex shrink-0 items-center gap-3">
                  <span className="text-xs font-medium text-[#4a3d66] dark:text-gray-400 tabular-nums">
                    {appLang === 'ka'
                      ? `ნაბიჯი ${step + 1}/${steps.length}`
                      : `Step ${step + 1} of ${steps.length}`}
                  </span>
                  {isEdit && (
                    <Button
                      type="button"
                      onClick={() => submitEvent({ stay: true })}
                      disabled={loading}
                      className="h-9 shrink-0 rounded-full btn-gradient px-4 text-sm font-semibold text-white"
                      data-testid="save-changes-btn"
                    >
                      {loading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : appLang === 'ka' ? (
                        'შენახვა'
                      ) : (
                        'Save changes'
                      )}
                    </Button>
                  )}
                </div>
              </div>
              <div className="flex gap-1.5">
                {steps.map((s, i) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => goToStep(i)}
                    disabled={!isEdit && i > step}
                    aria-label={s.label}
                    aria-current={i === step ? 'step' : undefined}
                    className={`group flex-1 text-left ${isEdit || i < step ? 'cursor-pointer' : 'cursor-default'}`}
                    data-testid={`step-indicator-${s.id}`}
                  >
                    <span
                      className={`block h-1.5 rounded-full transition-colors ${
                        i <= step
                          ? 'bg-grad-hot'
                          : 'bg-[#1b1033]/10 dark:bg-white/10'
                      }`}
                    />
                    {/* Step name shown inside each segment (not just a counter) */}
                    <span
                      className={`mt-1.5 block truncate text-[10px] font-medium leading-tight tracking-tight transition-colors sm:text-[11px] ${
                        i === step
                          ? 'text-[#ff4d8d]'
                          : i < step
                            ? 'text-[#4a3d66] dark:text-gray-300'
                            : 'text-[#4a3d66]/70 dark:text-gray-500'
                      }`}
                    >
                      {s.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Step 1 — About */}
            {currentStep.id === 'about' && (
              <div className="space-y-6 animate-fade-in">
                {/* Who is this event for? */}
                <div>
                  <Label className="text-sm font-medium text-[#1b1033]/70 dark:text-gray-400 mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    {appLang === 'ka' ? 'ვისთვის არის ეს ღონისძიება?' : 'Who is this event for?'}
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setEventFor('self')
                        setFormData((prev: any) => ({ ...prev, honoree_name: '', honoree_name_en: '' }))
                      }}
                      className={`p-4 rounded-2xl border-2 text-center font-medium transition-all ${
                        eventFor === 'self'
                          ? 'border-[#ff4d8d] ring-2 ring-[#ff4d8d]/25 bg-[#ff4d8d]/[0.06] text-[#1b1033] dark:text-[#f4f0ff]'
                          : 'border-gray-200 dark:border-white/10 text-[#4a3d66] dark:text-gray-300 hover:border-[#1b1033]/20'
                      }`}
                      data-testid="event-for-self"
                    >
                      {appLang === 'ka' ? 'ჩემთვის' : 'For myself'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEventFor('other')}
                      className={`p-4 rounded-2xl border-2 text-center font-medium transition-all ${
                        eventFor === 'other'
                          ? 'border-[#ff4d8d] ring-2 ring-[#ff4d8d]/25 bg-[#ff4d8d]/[0.06] text-[#1b1033] dark:text-[#f4f0ff]'
                          : 'border-gray-200 dark:border-white/10 text-[#4a3d66] dark:text-gray-300 hover:border-[#1b1033]/20'
                      }`}
                      data-testid="event-for-other"
                    >
                      {appLang === 'ka' ? 'სხვისთვის' : 'For someone else'}
                    </button>
                  </div>
                  {eventFor === 'other' && (
                    <div className="mt-3 animate-fade-in">
                      <Input
                        value={formData.honoree_name}
                        onChange={(e) =>
                          setFormData({ ...formData, honoree_name: e.target.value })
                        }
                        placeholder={
                          appLang === 'ka'
                            ? 'ვის აღვნიშნავთ? (მაგ., მარიამი)'
                            : 'Who are you celebrating? (e.g., Mariam)'
                        }
                        maxLength={120}
                        className="h-12 bg-white dark:bg-white/5 border-[#1b1033]/12 dark:border-white/10 rounded-xl"
                        data-testid="event-honoree-input"
                      />
                      <p className="text-xs text-[#4a3d66] dark:text-gray-400 mt-1">
                        {appLang === 'ka'
                          ? 'გამოჩნდება მოწვევაში, როგორც ღირსეული სტუმარი.'
                          : 'Shown on the invitation as the guest of honor.'}
                      </p>
                      {formData.language === 'both' && (
                        <Input
                          value={formData.honoree_name_en}
                          onChange={(e) =>
                            setFormData({ ...formData, honoree_name_en: e.target.value })
                          }
                          placeholder={
                            appLang === 'ka'
                              ? 'სახელი ინგლისურად (მაგ., Mariam)'
                              : 'Name in English (e.g., Mariam)'
                          }
                          maxLength={120}
                          className="mt-2 h-12 bg-white dark:bg-white/5 border-[#1b1033]/12 dark:border-white/10 rounded-xl"
                          data-testid="event-honoree-en-input"
                        />
                      )}
                    </div>
                  )}
                </div>

                {/* Event Type — picked first so the title placeholder can adapt */}
                <div>
                  <Label className="text-sm font-medium text-[#1b1033]/70 dark:text-gray-400 mb-1.5 block">
                    {t.eventType} *
                  </Label>
                  <Select
                    value={formData.event_type}
                    onValueChange={handleEventTypeSelect}
                  >
                    <SelectTrigger
                      className="h-12 bg-white dark:bg-white/5 border-[#1b1033]/12 dark:border-white/10 rounded-xl"
                      data-testid="event-type-select"
                    >
                      <SelectValue placeholder={t.selectEventType} />
                    </SelectTrigger>
                    <SelectContent>
                      {EVENT_TYPES.map((type) => (
                        <SelectItem key={type.en} value={type.en}>
                          {appLang === 'ka' ? type.ka : type.en}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Title */}
                <div>
                  <Label
                    htmlFor="title"
                    className="text-sm font-medium text-[#1b1033]/70 dark:text-gray-400 mb-1.5 block"
                  >
                    {t.eventTitle} *
                  </Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    placeholder={
                      titleExampleFor(formData.event_type, appLang) ||
                      t.eventTitlePlaceholder
                    }
                    maxLength={120}
                    className="h-12 bg-white dark:bg-white/5 border-[#1b1033]/12 dark:border-white/10 rounded-xl focus-visible:ring-2 focus-visible:ring-[#ff4d8d]/25 focus-visible:border-[#ff4d8d]/40"
                    required
                    data-testid="event-title-input"
                  />
                  <p className="text-xs text-[#4a3d66] dark:text-gray-400 mt-1">
                    {t.eventTitleHint}
                  </p>
                  {formData.language === 'both' && (
                    <div className="mt-3 animate-fade-in">
                      <Label
                        htmlFor="title_en"
                        className="text-sm font-medium text-[#1b1033]/70 dark:text-gray-400 mb-1.5 block"
                      >
                        {appLang === 'ka' ? 'სათაური ინგლისურად' : 'Title in English'}
                      </Label>
                      <Input
                        id="title_en"
                        value={formData.title_en}
                        onChange={(e) =>
                          setFormData({ ...formData, title_en: e.target.value })
                        }
                        placeholder={"e.g., Ana & Dato's Wedding"}
                        maxLength={120}
                        className="h-12 bg-white dark:bg-white/5 border-[#1b1033]/12 dark:border-white/10 rounded-xl"
                        data-testid="event-title-en-input"
                      />
                    </div>
                  )}
                </div>

                {/* Description */}
                <div>
                  <Label
                    htmlFor="description"
                    className="text-sm font-medium text-[#1b1033]/70 dark:text-gray-400 mb-1.5 block"
                  >
                    {t.description}
                  </Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder={t.descriptionPlaceholder}
                    maxLength={2000}
                    className="bg-white dark:bg-white/5 border-[#1b1033]/12 dark:border-white/10 rounded-xl min-h-[120px] resize-none"
                    data-testid="event-description-input"
                  />
                  {formData.language === 'both' && (
                    <div className="mt-3 animate-fade-in">
                      <Label
                        htmlFor="description_en"
                        className="text-sm font-medium text-[#1b1033]/70 dark:text-gray-400 mb-1.5 block"
                      >
                        {appLang === 'ka' ? 'აღწერა ინგლისურად' : 'Description in English'}
                      </Label>
                      <Textarea
                        id="description_en"
                        value={formData.description_en}
                        onChange={(e) =>
                          setFormData({ ...formData, description_en: e.target.value })
                        }
                        placeholder={
                          appLang === 'ka'
                            ? 'იგივე აღწერა ინგლისურენოვანი სტუმრებისთვის'
                            : 'The same description for English-speaking guests'
                        }
                        maxLength={2000}
                        className="bg-white dark:bg-white/5 border-[#1b1033]/12 dark:border-white/10 rounded-xl min-h-[90px] resize-none"
                        data-testid="event-description-en-input"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 2 — When & where */}
            {currentStep.id === 'when' && (
              <div className="space-y-6 animate-fade-in">
                {/* Date range + times */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-[#1b1033]/70 dark:text-gray-400 mb-1.5 block">
                      {t.date} *
                    </Label>
                    <DateRangePicker
                      from={formData.date}
                      to={formData.end_date}
                      min={today}
                      language={appLang}
                      data-testid="event-date-input"
                      onChange={({ from, to }) =>
                        setFormData((prev: any) => ({
                          ...prev,
                          date: from,
                          end_date: to && to !== from ? to : '',
                          rsvp_deadline:
                            prev.rsvp_deadline && from && prev.rsvp_deadline > from
                              ? ''
                              : prev.rsvp_deadline,
                        }))
                      }
                    />
                    <p className="text-xs text-[#4a3d66] dark:text-gray-400 mt-1">
                      {appLang === 'ka'
                        ? 'აირჩიეთ ერთი დღე ან დიაპაზონი (დან–მდე)'
                        : 'Pick a single day, or a range for multi-day events'}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-[#1b1033]/70 dark:text-gray-400 mb-1.5 block">
                        {appLang === 'ka' ? 'დაწყების დრო' : 'Start time'} *
                      </Label>
                      <TimePicker
                        value={formData.time}
                        onChange={(time) =>
                          setFormData((prev: any) => {
                            const sameDay = !prev.end_date || prev.end_date === prev.date
                            // If the start moves past a same-day end time, clear the end.
                            const end_time =
                              sameDay && prev.end_time && prev.end_time < time ? '' : prev.end_time
                            return { ...prev, time, end_time }
                          })
                        }
                        data-testid="event-time-input"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-[#1b1033]/70 dark:text-gray-400 mb-1.5 block">
                        {appLang === 'ka' ? 'დასრულების დრო' : 'End time'}
                      </Label>
                      <TimePicker
                        value={formData.end_time}
                        min={
                          !formData.end_date || formData.end_date === formData.date
                            ? formData.time
                            : undefined
                        }
                        onChange={(end_time) => setFormData((prev: any) => ({ ...prev, end_time }))}
                        data-testid="event-end-time-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Location */}
                <div>
                  <Label
                    htmlFor="location"
                    className="text-sm font-medium text-[#1b1033]/70 dark:text-gray-400 mb-1.5 block"
                  >
                    {t.location} *
                  </Label>
                  <div className="relative">
                    <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      ref={locationInputRef}
                      id="location"
                      value={formData.location}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          location: e.target.value,
                          latitude: null,
                          longitude: null,
                        })
                      }
                      placeholder={t.locationPlaceholder}
                      maxLength={300}
                      className="pl-12 h-12 bg-white dark:bg-white/5 border-[#1b1033]/12 dark:border-white/10 rounded-xl"
                      autoComplete="off"
                      required
                      data-testid="event-location-input"
                    />
                  </div>
                  <p className="text-xs text-[#4a3d66] dark:text-gray-400 mt-1">
                    {getGoogleMapsApiKey()
                      ? appLang === 'ka'
                        ? 'დაიწყე აკრეფა და აირჩიე მისამართი Google Maps-ის ვარიანტებიდან'
                        : 'Start typing to pick an address from Google Maps suggestions'
                      : appLang === 'ka'
                        ? 'შეიყვანე მისამართი'
                        : 'Enter the address for your event'}
                  </p>
                  {getGoogleMapsApiKey() ? (
                    <div className="mt-4 overflow-hidden rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1e1438] shadow-sm">
                      <div className="flex items-center justify-between border-b border-gray-100 dark:border-white/10 px-4 py-3">
                        <div>
                          <p className="text-sm font-semibold text-[#1b1033] dark:text-[#f4f0ff]">
                            {appLang === 'ka' ? 'მონიშნე რუკაზე' : 'Pin the location'}
                          </p>
                          <p className="text-xs text-[#4a3d66] dark:text-gray-400">
                            {formData.latitude != null && formData.longitude != null
                              ? appLang === 'ka'
                                ? 'ზუსტი მდებარეობა — გადაათრიე პინი დასაზუსტებლად'
                                : 'Exact location — drag the pin to fine-tune'
                              : appLang === 'ka'
                                ? 'დააჭირე რუკას პინის დასასმელად'
                                : 'Tap the map to drop a pin'}
                          </p>
                        </div>
                        <div className="rounded-full bg-[#ff4d8d]/[0.06] px-3 py-1 text-xs font-medium text-[#ff4d8d]">
                          Google Maps
                        </div>
                      </div>
                      <div className="p-3">
                        <LocationPickerMap
                          latitude={formData.latitude}
                          longitude={formData.longitude}
                          language={appLang}
                          onPick={({ latitude, longitude, location }) =>
                            setFormData((prev: any) => ({
                              ...prev,
                              latitude,
                              longitude,
                              location: location || prev.location,
                            }))
                          }
                        />
                      </div>
                    </div>
                  ) : mapPreviewUrl ? (
                    <div className="mt-4 overflow-hidden rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1e1438] shadow-sm">
                      <iframe
                        title="Google Maps preview"
                        src={mapPreviewUrl}
                        className="h-72 w-full border-0"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                      />
                    </div>
                  ) : null}
                </div>

                {/* RSVP Deadline */}
                <div>
                  <Label className="text-sm font-medium text-[#1b1033]/70 dark:text-gray-400 mb-1.5 block">
                    {t.rsvpDeadline}
                  </Label>
                  <DatePicker
                    value={formData.rsvp_deadline}
                    min={today}
                    max={formData.date || undefined}
                    language={appLang}
                    placeholder={appLang === 'ka' ? 'არასავალდებულო' : 'Optional'}
                    onChange={(rsvp_deadline) =>
                      setFormData((prev: any) => ({ ...prev, rsvp_deadline }))
                    }
                    data-testid="event-rsvp-deadline-input"
                  />
                  <p className="text-xs text-[#4a3d66] dark:text-gray-400 mt-1">{t.rsvpDeadlineHint}</p>
                </div>

                {/* Arrival instructions */}
                <div>
                  <Label
                    htmlFor="arrival_instructions"
                    className="text-sm font-medium text-[#1b1033]/70 dark:text-gray-400 mb-1.5 block"
                  >
                    {appLang === 'ka' ? 'მისვლის ინსტრუქცია' : 'Arrival instructions'}
                  </Label>
                  <Textarea
                    id="arrival_instructions"
                    value={formData.arrival_instructions}
                    onChange={(e) =>
                      setFormData({ ...formData, arrival_instructions: e.target.value })
                    }
                    placeholder={
                      appLang === 'ka'
                        ? 'პარკინგი, შესასვლელი, ტრანსპორტი, რა წამოიღონ...'
                        : 'Parking, which entrance, public transport, what to bring...'
                    }
                    maxLength={1200}
                    className="bg-white dark:bg-white/5 border-[#1b1033]/12 dark:border-white/10 rounded-xl min-h-[90px] resize-none"
                    data-testid="event-arrival-input"
                  />
                  <p className="text-xs text-[#4a3d66] dark:text-gray-400 mt-1">
                    {appLang === 'ka'
                      ? 'არასავალდებულო — მიმართულება, პარკინგი ან ტრანსპორტი სტუმრებისთვის.'
                      : 'Optional — directions, parking or transport tips for your guests.'}
                  </p>
                  {formData.language === 'both' && (
                    <div className="mt-3 animate-fade-in">
                      <Label
                        htmlFor="arrival_instructions_en"
                        className="text-sm font-medium text-[#1b1033]/70 dark:text-gray-400 mb-1.5 block"
                      >
                        {appLang === 'ka'
                          ? 'მისვლის ინსტრუქცია ინგლისურად'
                          : 'Arrival instructions in English'}
                      </Label>
                      <Textarea
                        id="arrival_instructions_en"
                        value={formData.arrival_instructions_en}
                        onChange={(e) =>
                          setFormData({ ...formData, arrival_instructions_en: e.target.value })
                        }
                        placeholder="Parking, which entrance, public transport, what to bring..."
                        maxLength={1200}
                        className="bg-white dark:bg-white/5 border-[#1b1033]/12 dark:border-white/10 rounded-xl min-h-[70px] resize-none"
                        data-testid="event-arrival-en-input"
                      />
                    </div>
                  )}
                </div>

                {/* Day schedule / itinerary — optional, for multi-step events */}
                <div>
                  <Label className="text-sm font-medium text-[#1b1033]/70 dark:text-gray-400 mb-1.5 block">
                    {appLang === 'ka' ? 'დღის განრიგი' : 'Day schedule'}
                  </Label>
                  <p className="text-xs text-[#4a3d66] dark:text-gray-400 mb-3">
                    {appLang === 'ka'
                      ? 'არასავალდებულო — მრავალსაფეხურიანი ღონისძიებისთვის დაამატე პუნქტები (დრო, აქტივობა, ადგილი). სტუმრები მათ მოსაწვევზე დაინახავენ.'
                      : 'Optional — for multi-step events, add agenda items (time, activity, place). Guests see them on the invitation.'}
                  </p>
                  <div className="space-y-3">
                    {(formData.itinerary || []).map((row: any, i: number) => (
                      <div
                        key={i}
                        className="p-3 rounded-xl border border-[#1b1033]/12 dark:border-white/10 bg-white dark:bg-white/5 space-y-2 animate-fade-in"
                        data-testid={`itinerary-row-${i}`}
                      >
                        <div className="flex items-center gap-2">
                          <Input
                            type="time"
                            value={row.start}
                            onChange={(e) => updateItineraryRow(i, { start: e.target.value })}
                            className="h-10 w-28 rounded-lg"
                            data-testid={`itinerary-start-${i}`}
                          />
                          <span className="text-[#4a3d66] dark:text-gray-400">–</span>
                          <Input
                            type="time"
                            value={row.end}
                            onChange={(e) => updateItineraryRow(i, { end: e.target.value })}
                            className="h-10 w-28 rounded-lg"
                            data-testid={`itinerary-end-${i}`}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="ml-auto text-[#4a3d66] dark:text-gray-400 hover:text-red-500"
                            onClick={() => removeItineraryRow(i)}
                            data-testid={`itinerary-remove-${i}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <Input
                          value={row.title}
                          onChange={(e) => updateItineraryRow(i, { title: e.target.value })}
                          placeholder={
                            appLang === 'ka'
                              ? 'აქტივობა (მაგ., სტუმრების მიღება)'
                              : 'Activity (e.g., Guest arrival)'
                          }
                          maxLength={160}
                          className="h-10 rounded-lg"
                          data-testid={`itinerary-title-${i}`}
                        />
                        <Input
                          value={row.location}
                          onChange={(e) => updateItineraryRow(i, { location: e.target.value })}
                          placeholder={
                            appLang === 'ka'
                              ? 'ადგილი (მაგ., მთავარი დარბაზი) — არასავალდებულო'
                              : 'Place (e.g., Main hall) — optional'
                          }
                          maxLength={200}
                          className="h-10 rounded-lg"
                          data-testid={`itinerary-location-${i}`}
                        />
                        {formData.language === 'both' && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <Input
                              value={row.title_en}
                              onChange={(e) => updateItineraryRow(i, { title_en: e.target.value })}
                              placeholder={
                                appLang === 'ka' ? 'აქტივობა ინგლისურად' : 'Activity in English'
                              }
                              maxLength={160}
                              className="h-10 rounded-lg"
                              data-testid={`itinerary-title-en-${i}`}
                            />
                            <Input
                              value={row.location_en}
                              onChange={(e) =>
                                updateItineraryRow(i, { location_en: e.target.value })
                              }
                              placeholder={
                                appLang === 'ka' ? 'ადგილი ინგლისურად' : 'Place in English'
                              }
                              maxLength={200}
                              className="h-10 rounded-lg"
                              data-testid={`itinerary-location-en-${i}`}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-xl"
                      onClick={addItineraryRow}
                      data-testid="itinerary-add-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      {appLang === 'ka' ? 'პუნქტის დამატება' : 'Add step'}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3 — Design */}
            {currentStep.id === 'design' && (
              <div className={`animate-fade-in lg:grid lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-8 ${previewColumn}`}>
              <div className="min-w-0 space-y-6">
                {/* Invitation design — the template guests actually open,
                    plus the colour, background and font that repaint it. Kept
                    at the top of the step because everything below (cover
                    photo, language) is decoration on top of this choice. */}
                <InviteTemplatePicker
                  appLang={appLang}
                  invLang={invLang}
                  words={previewWords}
                  value={{
                    template: formData.invite_template,
                    accent: formData.accent_color,
                    background: formData.background_color,
                    font: formData.invite_font,
                  }}
                  onChange={(patch) =>
                    setFormData((prev: any) => ({
                      ...prev,
                      ...('template' in patch ? { invite_template: patch.template } : {}),
                      ...('accent' in patch ? { accent_color: patch.accent } : {}),
                      ...('background' in patch ? { background_color: patch.background } : {}),
                      ...('font' in patch ? { invite_font: patch.font } : {}),
                    }))
                  }
                />

                {/* AI invitation designer — generate a background + font pairing */}
                {SHOW_AI_DESIGN_TOOLS && (
                <AiDesignStudio
                  appLang={appLang}
                  context={{
                    title: formData.title,
                    event_type: formData.event_type,
                    language: formData.language,
                  }}
                  applied={{
                    cover_image: formData.cover_image,
                    font_heading: formData.font_heading,
                    font_body: formData.font_body,
                  }}
                  onApply={(design: any) =>
                    setFormData((prev: any) => ({ ...prev, ...design }))
                  }
                />
                )}

                {/* Cover Image Upload — a painted design is a finished picture
                    with no place for a photo, so it offers none. */}
                {!designTemplate.painted && (
                <div>
                  <Label className="text-sm font-medium text-[#1b1033]/70 dark:text-gray-400 mb-3 flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    {t.coverImage}
                  </Label>

                  {/* Drag & Drop Zone */}
                  <div
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all mb-3 ${
                      dragActive
                        ? 'border-[#ff4d8d] bg-[#ff4d8d]/[0.06]'
                        : 'border-gray-200 dark:border-white/10 hover:border-[#1b1033]/20'
                    }`}
                  >
                    {uploading ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-8 h-8 animate-spin text-[#ff4d8d]" />
                      </div>
                    ) : formData.cover_image ? (
                      <div className="relative">
                        <img
                          src={resolveImageUrl(formData.cover_image)}
                          alt="Cover"
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute top-2 right-2 bg-white/90 dark:bg-[#1e1438]/90 hover:bg-white dark:hover:bg-[#1e1438] rounded-full"
                          onClick={(e) => {
                            e.stopPropagation()
                            setFormData({
                              ...formData,
                              cover_image: '',
                            })
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-[#4a3d66] dark:text-gray-400">{t.dragDropImage}</p>
                      </>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e.target.files?.[0])}
                      className="hidden"
                    />
                  </div>

                </div>
                )}

                {/* Design & animation — one consolidated module: theme and
                    emoji-animation style as dropdowns, next to a live preview
                    that re-renders as the host designs. */}
                {SHOW_AI_DESIGN_TOOLS && (
                <div>
                  <Label className="text-sm font-medium text-[#1b1033]/70 dark:text-gray-400 mb-3 flex items-center gap-2">
                    <Palette className="w-4 h-4" />
                    {appLang === 'ka' ? 'დიზაინი და ანიმაცია' : 'Design & animation'}
                  </Label>
                  <div className="grid items-start gap-5 sm:grid-cols-2">
                    <div className="space-y-4">
                      {/* Theme dropdown */}
                      <div>
                        <p className="mb-1.5 text-xs font-medium text-[#1b1033]/60 dark:text-gray-400">
                          {t.theme}
                        </p>
                        <Select value={formData.theme} onValueChange={handleThemeSelect}>
                          <SelectTrigger
                            className="w-full rounded-xl bg-white dark:bg-white/5 border-[#1b1033]/12 dark:border-white/10"
                            data-testid="theme-select"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {THEMES.map((theme) => (
                              <SelectItem
                                key={theme.id}
                                value={theme.id}
                                textValue={theme.name[appLang] || theme.name.en}
                                data-testid={`theme-option-${theme.id}`}
                              >
                                <span className="flex items-center gap-2">
                                  <span
                                    className={`h-4 w-4 shrink-0 rounded-full bg-gradient-to-br ${theme.preview} ring-1 ring-black/10 dark:ring-white/20`}
                                  />
                                  <span className="truncate">
                                    {theme.name[appLang] || theme.name.en}
                                  </span>
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="mt-1 text-[11px] text-[#4a3d66] dark:text-gray-400">
                          {selectedTheme.style[appLang] || selectedTheme.style.en}
                        </p>
                      </div>

                      {/* Animation dropdown */}
                      <div>
                        <p className="mb-1.5 flex items-center gap-1 text-xs font-medium text-[#1b1033]/60 dark:text-gray-400">
                          <Sparkles className="h-3 w-3" />
                          {appLang === 'ka' ? 'ანიმაცია' : 'Animation'}
                        </p>
                        <Select
                          value={formData.animation_style}
                          onValueChange={(v) =>
                            setFormData({ ...formData, animation_style: v })
                          }
                        >
                          <SelectTrigger
                            className="w-full rounded-xl bg-white dark:bg-white/5 border-[#1b1033]/12 dark:border-white/10"
                            data-testid="animation-select"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ANIMATION_STYLES.map((style) => (
                              <SelectItem
                                key={style.id}
                                value={style.id}
                                textValue={style.label[appLang] || style.label.en}
                                data-testid={`animation-option-${style.id}`}
                              >
                                <span className="flex items-center gap-2">
                                  <span className="shrink-0">{style.icon}</span>
                                  <span className="truncate">
                                    {style.label[appLang] || style.label.en}
                                  </span>
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="mt-1 text-[11px] text-[#4a3d66] dark:text-gray-400">
                          {appLang === 'ka'
                            ? 'მოცურავე ემოჯი-ანიმაცია სტუმრის მოსაწვევზე.'
                            : 'Floating emoji on the guest invitation.'}
                        </p>
                      </div>
                    </div>

                    {/* Live preview — updates with theme, image, fonts & animation */}
                    <InvitationLivePreview
                      formData={formData}
                      theme={selectedTheme}
                      appLang={appLang}
                    />
                  </div>
                </div>
                )}

                {/* Language Selection */}
                <div>
                  <Label className="text-sm font-medium text-[#1b1033]/70 dark:text-gray-400 mb-3 flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    {t.language}
                  </Label>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant={formData.language === 'both' ? 'default' : 'outline'}
                      onClick={() => setFormData({ ...formData, language: 'both' })}
                      className={`flex-1 rounded-xl ${formData.language === 'both' ? 'bg-[#ff4d8d]' : ''}`}
                    >
                      {appLang === 'ka' ? 'ორივე' : 'Both'}
                    </Button>
                    <Button
                      type="button"
                      variant={formData.language === 'ka' ? 'default' : 'outline'}
                      onClick={() => setFormData({ ...formData, language: 'ka' })}
                      className={`flex-1 rounded-xl ${formData.language === 'ka' ? 'bg-[#ff4d8d]' : ''}`}
                    >
                      ქართული
                    </Button>
                    <Button
                      type="button"
                      variant={formData.language === 'en' ? 'default' : 'outline'}
                      onClick={() => setFormData({ ...formData, language: 'en' })}
                      className={`flex-1 rounded-xl ${formData.language === 'en' ? 'bg-[#ff4d8d]' : ''}`}
                    >
                      English
                    </Button>
                  </div>
                  <p className="text-xs text-[#4a3d66] dark:text-gray-400 mt-2">
                    {appLang === 'ka'
                      ? '„ორივე“-ს არჩევისას სტუმრები მოსაწვევზე ენის გადამრთველს დაინახავენ — ძირითადი ველები შეავსე ქართულად, ინგლისური ვერსიები კი თითოეულ ველთანაა.'
                      : 'With “Both”, guests get a language switcher on the invitation — fill the main fields in Georgian and add the English versions offered next to each field.'}
                  </p>
                </div>

              </div>

              {/* Full-height column with the preview sticky inside it: as a
                  sticky grid item it could only travel inside its own grid
                  area, so it scrolled away whenever the designer column was
                  the shorter one. The offset clears the step header, which in
                  edit mode is sticky above it. */}
              <aside id="design-preview" className={`mt-8 lg:mt-0 ${isEdit ? 'scroll-mt-44' : 'scroll-mt-24'}`}>
                <div
                  className={`lg:sticky ${isEdit ? 'lg:top-44' : 'lg:top-24'}`}
                  style={{ '--preview-top': isEdit ? '11rem' : '6rem' } as any}
                >
                <InvitePreviewFrame
                  template={formData.invite_template}
                  accent={formData.accent_color}
                  background={formData.background_color}
                  font={formData.invite_font}
                  lang={invLang}
                  appLang={appLang}
                  cover={formData.cover_image}
                  words={previewWords}
                  onDevice={setPreviewDevice}
                />
                </div>
              </aside>
              </div>
            )}

            {/* Step 4 — Guest options */}
            {currentStep.id === 'options' && (
              <div className="space-y-6 animate-fade-in">
                {/* Guest Sharing Permission — the whole card toggles the checkbox */}
                <label
                  htmlFor="allow_guest_sharing"
                  className="flex items-start gap-3 p-4 bg-celebration-soft/60 dark:bg-white/5 rounded-2xl border border-[#1b1033]/8 dark:border-white/10 cursor-pointer select-none transition-colors hover:bg-celebration-soft dark:hover:bg-white/[0.07]"
                >
                  <Checkbox
                    id="allow_guest_sharing"
                    checked={formData.allow_guest_sharing}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, allow_guest_sharing: checked })
                    }
                    className="mt-0.5"
                    data-testid="allow-guest-sharing-checkbox"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-[#1b1033]/70 dark:text-gray-400 flex items-center gap-2">
                      <Share2 className="w-4 h-4 text-[#4a3d66] dark:text-gray-400" />
                      {appLang === 'ka'
                        ? 'სტუმრებს შეუძლიათ მოწვევის გაზიარება'
                        : 'Allow guests to share invitation'}
                    </span>
                    <p className="text-xs text-[#4a3d66] dark:text-gray-400 mt-1">
                      {appLang === 'ka'
                        ? 'თუ გამორთულია, მხოლოდ შენ შეგიძლია მოწვევის გაზიარება'
                        : 'If disabled, only you can share the invitation link'}
                    </p>
                  </div>
                </label>

                {/* Plus-one permission — the whole card toggles the checkbox */}
                <label
                  htmlFor="allow_plus_one"
                  className="flex items-start gap-3 p-4 bg-celebration-soft/60 dark:bg-white/5 rounded-2xl border border-[#1b1033]/8 dark:border-white/10 cursor-pointer select-none transition-colors hover:bg-celebration-soft dark:hover:bg-white/[0.07]"
                >
                  <Checkbox
                    id="allow_plus_one"
                    checked={formData.allow_plus_one}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, allow_plus_one: checked })
                    }
                    className="mt-0.5"
                    data-testid="allow-plus-one-checkbox"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-[#1b1033]/70 dark:text-gray-400 flex items-center gap-2">
                      <Users className="w-4 h-4 text-[#4a3d66] dark:text-gray-400" />
                      {appLang === 'ka'
                        ? 'სტუმრებს შეუძლიათ თანმხლები პირის (+1) მოყვანა'
                        : 'Allow guests to bring a plus one'}
                    </span>
                    <p className="text-xs text-[#4a3d66] dark:text-gray-400 mt-1">
                      {appLang === 'ka'
                        ? 'თუ გამორთულია, RSVP ფორმაში +1 ვერ მოინიშნება'
                        : "If disabled, guests won't see the +1 option when responding"}
                    </p>
                  </div>
                </label>

                {/* Gifts section toggle. It lives here rather than on the
                    create-only Gifts step so an existing event can turn it
                    back on: for types where a registry is out of place
                    (corporate, charity) it starts off, and the host may still
                    want one — or may want to drop one they set up. */}
                <label
                  htmlFor="show_gifts"
                  className="flex items-start gap-3 p-4 bg-celebration-soft/60 dark:bg-white/5 rounded-2xl border border-[#1b1033]/8 dark:border-white/10 cursor-pointer select-none transition-colors hover:bg-celebration-soft dark:hover:bg-white/[0.07]"
                >
                  <Checkbox
                    id="show_gifts"
                    checked={formData.show_gifts}
                    onCheckedChange={(checked) => {
                      setGiftsTouched(true)
                      setFormData({ ...formData, show_gifts: checked === true })
                    }}
                    className="mt-0.5"
                    data-testid="show-gifts-checkbox"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-[#1b1033]/70 dark:text-gray-400 flex items-center gap-2">
                      <Gift className="w-4 h-4 text-[#4a3d66] dark:text-gray-400" />
                      {appLang === 'ka'
                        ? 'საჩუქრების სექციის ჩვენება მოსაწვევში'
                        : 'Show a gifts section on the invitation'}
                    </span>
                    <p className="text-xs text-[#4a3d66] dark:text-gray-400 mt-1">
                      {appLang === 'ka'
                        ? 'თუ გამორთულია, საჩუქრების სია სტუმრებს არ გამოუჩნდებათ.'
                        : "If off, the gifts list won't be shown to guests on the invitation."}
                    </p>
                    {/* Why it came pre-set that way — the host can override. */}
                    {!eventTypeWantsGifts(formData.event_type) && !formData.show_gifts && (
                      <p
                        className="text-xs text-[#4a3d66] dark:text-gray-400 mt-2 rounded-lg bg-[#1b1033]/[0.06] dark:bg-white/10 px-2.5 py-2"
                        data-testid="gifts-not-typical-note"
                      >
                        {appLang === 'ka'
                          ? `„${localizeEventType(formData.event_type, 'ka')}“-სთვის საჩუქრების სია ჩვეულებრივ არ გამოიყენება, ამიტომ თავიდან გამორთულია. სტუმრების სია და RSVP მუშაობს ჩვეულებრივად.`
                          : `A gift list is not usual for a ${localizeEventType(formData.event_type, 'en').toLowerCase()}, so it starts off. Your guest list and RSVPs work as normal.`}
                      </p>
                    )}
                  </div>
                </label>

                {/* RSVP closing controls — three independent ways to stop or
                    restrict responses, all separate from the deadline date. */}
                <div className="rounded-2xl border border-[#1b1033]/8 dark:border-white/10 p-4 space-y-4">
                  <Label className="text-sm font-medium text-[#1b1033]/70 dark:text-gray-400 flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    {t.rsvpControls}
                  </Label>

                  <label
                    htmlFor="rsvp_closed_manual"
                    className="flex items-start gap-3 p-3 bg-celebration-soft/60 dark:bg-white/5 rounded-xl cursor-pointer select-none transition-colors hover:bg-celebration-soft dark:hover:bg-white/[0.07]"
                  >
                    <Checkbox
                      id="rsvp_closed_manual"
                      checked={formData.rsvp_closed_manual}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, rsvp_closed_manual: checked })
                      }
                      className="mt-0.5"
                      data-testid="rsvp-closed-manual-checkbox"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-[#1b1033]/70 dark:text-gray-400">
                        {t.closeRsvpNow}
                      </span>
                      <p className="text-xs text-[#4a3d66] dark:text-gray-400 mt-1">
                        {t.closeRsvpNowHelp}
                      </p>
                    </div>
                  </label>

                  <label
                    htmlFor="rsvp_invite_only"
                    className="flex items-start gap-3 p-3 bg-celebration-soft/60 dark:bg-white/5 rounded-xl cursor-pointer select-none transition-colors hover:bg-celebration-soft dark:hover:bg-white/[0.07]"
                  >
                    <Checkbox
                      id="rsvp_invite_only"
                      checked={formData.rsvp_invite_only}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, rsvp_invite_only: checked })
                      }
                      className="mt-0.5"
                      data-testid="rsvp-invite-only-checkbox"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-[#1b1033]/70 dark:text-gray-400">
                        {t.inviteOnly}
                      </span>
                      <p className="text-xs text-[#4a3d66] dark:text-gray-400 mt-1">
                        {t.inviteOnlyHelp}
                      </p>
                      {/* Invite-only is useless without a guest list, and the
                          list lives on the event page — say so here, where the
                          host turns the mode on, instead of leaving them to
                          find out that nobody can RSVP. */}
                      {formData.rsvp_invite_only && (
                        <p
                          className="mt-2 rounded-lg bg-[#8b5cf6]/10 px-2.5 py-2 text-xs text-[#4a3d66] dark:bg-[#8b5cf6]/15 dark:text-[#c3b8dd]"
                          data-testid="invite-only-guest-list-note"
                        >
                          {appLang === 'ka'
                            ? 'დაამატე სტუმრები ღონისძიების გვერდზე — თითოეული ავტომატურად მიიღებს პირად კოდს, რომელსაც იქვე დააკოპირებ და გაუგზავნი.'
                            : 'Add your guests on the event page once it exists — each one gets a personal code there that you copy and send with the invitation.'}
                        </p>
                      )}
                    </div>
                  </label>

                  <div>
                    <Label
                      htmlFor="guest_capacity"
                      className="text-sm font-medium text-[#1b1033]/70 dark:text-gray-400 mb-1.5 block"
                    >
                      {t.guestCapacity}
                    </Label>
                    <Input
                      id="guest_capacity"
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={100000}
                      value={formData.guest_capacity}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          // Digits only: a stray "-" or "e" would otherwise
                          // reach the API as an unparseable capacity.
                          guest_capacity: e.target.value.replace(/[^0-9]/g, ''),
                        })
                      }
                      placeholder={t.guestCapacityPlaceholder}
                      className="h-12 bg-white dark:bg-white/5 border-[#1b1033]/12 dark:border-white/10 rounded-xl"
                      data-testid="event-guest-capacity-input"
                    />
                    <p className="text-xs text-[#4a3d66] dark:text-gray-400 mt-1">
                      {t.guestCapacityHelp}
                    </p>
                  </div>
                </div>

                {/* Custom RSVP labels */}
                <div>
                  <Label className="text-sm font-medium text-[#1b1033]/70 dark:text-gray-400 mb-1.5 block">
                    {appLang === 'ka' ? 'RSVP ღილაკების წარწერები' : 'RSVP button labels'}
                  </Label>
                  <p className="text-xs text-[#4a3d66] dark:text-gray-400 mb-3">
                    {appLang === 'ka'
                      ? 'არასავალდებულო — შეცვალე „ალბათ“ და „ვერ მოვალ“ ღილაკების წარწერები.'
                      : 'Optional — customize the “Maybe” and “Can’t attend” labels guests see.'}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input
                      value={formData.rsvp_label_maybe}
                      onChange={(e) =>
                        setFormData({ ...formData, rsvp_label_maybe: e.target.value })
                      }
                      placeholder={t.rsvpMaybe}
                      maxLength={40}
                      className="h-12 bg-white dark:bg-white/5 border-[#1b1033]/12 dark:border-white/10 rounded-xl"
                      data-testid="event-rsvp-maybe-input"
                    />
                    <Input
                      value={formData.rsvp_label_decline}
                      onChange={(e) =>
                        setFormData({ ...formData, rsvp_label_decline: e.target.value })
                      }
                      placeholder={t.rsvpDecline}
                      maxLength={40}
                      className="h-12 bg-white dark:bg-white/5 border-[#1b1033]/12 dark:border-white/10 rounded-xl"
                      data-testid="event-rsvp-decline-input"
                    />
                  </div>
                  {formData.language === 'both' && (
                    <div className="mt-3 animate-fade-in">
                      <p className="text-xs text-[#4a3d66] dark:text-gray-400 mb-2">
                        {appLang === 'ka'
                          ? 'იგივე წარწერები ინგლისურად:'
                          : 'The same labels in English:'}
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Input
                          value={formData.rsvp_label_maybe_en}
                          onChange={(e) =>
                            setFormData({ ...formData, rsvp_label_maybe_en: e.target.value })
                          }
                          placeholder="Maybe"
                          maxLength={40}
                          className="h-12 bg-white dark:bg-white/5 border-[#1b1033]/12 dark:border-white/10 rounded-xl"
                          data-testid="event-rsvp-maybe-en-input"
                        />
                        <Input
                          value={formData.rsvp_label_decline_en}
                          onChange={(e) =>
                            setFormData({ ...formData, rsvp_label_decline_en: e.target.value })
                          }
                          placeholder="Can't attend"
                          maxLength={40}
                          className="h-12 bg-white dark:bg-white/5 border-[#1b1033]/12 dark:border-white/10 rounded-xl"
                          data-testid="event-rsvp-decline-en-input"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Dress code (optional) */}
                <div>
                  <Label
                    htmlFor="dress_code"
                    className="text-sm font-medium text-[#1b1033]/70 dark:text-gray-400 mb-1.5 block"
                  >
                    {appLang === 'ka' ? 'დრეს-კოდი' : 'Dress code'}
                  </Label>
                  <Input
                    id="dress_code"
                    value={formData.dress_code}
                    onChange={(e) =>
                      setFormData({ ...formData, dress_code: e.target.value })
                    }
                    placeholder={
                      appLang === 'ka'
                        ? 'მაგ. „კოქტეილის სტილი" ან „შავ-ჰალსტუხი"'
                        : "e.g. 'Cocktail attire' or 'Black tie'"
                    }
                    maxLength={160}
                    className="h-12 bg-white dark:bg-white/5 border-[#1b1033]/12 dark:border-white/10 rounded-xl"
                    data-testid="event-dress-code-input"
                  />
                  <p className="text-xs text-[#4a3d66] dark:text-gray-400 mt-1">
                    {appLang === 'ka'
                      ? 'არასავალდებულო — გამოჩნდება მოსაწვევში სტუმრებისთვის.'
                      : 'Optional — shown to guests on the invitation.'}
                  </p>
                  {formData.language === 'both' && (
                    <Input
                      value={formData.dress_code_en}
                      onChange={(e) =>
                        setFormData({ ...formData, dress_code_en: e.target.value })
                      }
                      placeholder={
                        appLang === 'ka'
                          ? 'დრეს-კოდი ინგლისურად (მაგ. Cocktail attire)'
                          : "Dress code in English (e.g. 'Cocktail attire')"
                      }
                      maxLength={160}
                      className="mt-2 h-12 bg-white dark:bg-white/5 border-[#1b1033]/12 dark:border-white/10 rounded-xl"
                      data-testid="event-dress-code-en-input"
                    />
                  )}
                </div>
              </div>
            )}

            {/* Step 5 — Gifts (create only). The show_gifts switch itself
                lives on the Guests step, which edit mode keeps — otherwise an
                event could never turn its gifts section back on. */}
            {currentStep.id === 'gifts' && (
              <div className="space-y-6 animate-fade-in">
                {!formData.show_gifts && (
                  <div
                    className="rounded-2xl border border-[#1b1033]/8 dark:border-white/10 bg-celebration-soft/60 dark:bg-white/5 p-4"
                    data-testid="gifts-off-note"
                  >
                    <Label className="mb-1.5 flex items-center gap-2 text-sm font-medium text-[#1b1033]/70 dark:text-gray-400">
                      <Gift className="h-4 w-4" />
                      {appLang === 'ka' ? 'საჩუქრები გამორთულია' : 'Gifts are off'}
                    </Label>
                    <p className="text-xs text-[#4a3d66] dark:text-gray-400">
                      {!eventTypeWantsGifts(formData.event_type)
                        ? appLang === 'ka'
                          ? `„${localizeEventType(formData.event_type, 'ka')}“-სთვის საჩუქრების სია ჩვეულებრივ არ გამოიყენება, ამიტომ თავიდან გამორთულია. სტუმრების სია და RSVP მუშაობს ჩვეულებრივად — ჩართვა შეგიძლია წინა ნაბიჯზე, „სტუმრები“.`
                          : `A gift list is not usual for a ${localizeEventType(formData.event_type, 'en').toLowerCase()}, so it starts off. Your guest list and RSVPs work as normal — turn gifts on from the previous step, Guests.`
                        : appLang === 'ka'
                          ? 'საჩუქრების სექცია სტუმრებს არ გამოუჩნდებათ. ჩართვა შეგიძლია წინა ნაბიჯზე, „სტუმრები“.'
                          : "Guests won't see a gifts section. Turn it on from the previous step, Guests."}
                    </p>
                  </div>
                )}

                {/* Gift Wishlist — only worth filling in when the invitation
                    will actually show it. */}
                {formData.show_gifts && (
                <div>
                  <Label className="text-sm font-medium text-[#1b1033]/70 dark:text-gray-400 mb-1.5 flex items-center gap-2">
                    <Gift className="w-4 h-4" />
                    {t.wishlist}
                  </Label>
                  <p className="text-xs text-[#4a3d66] dark:text-gray-400 mb-3">
                    {appLang === 'ka'
                      ? 'დაამატე საჩუქრები, რომელთა დაფინანსებაშიც სტუმრები მიიღებენ მონაწილეობას (არასავალდებულო)'
                      : 'Add gifts your guests can chip in for — optional, and you can edit them later'}
                  </p>

                  {/* Items added so far */}
                  {wishlistItems.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {wishlistItems.map((item, index) => {
                        return (
                          <div
                            key={index}
                            className="flex items-center gap-3 p-3 rounded-xl border border-[#1b1033]/10 dark:border-white/10 bg-white dark:bg-white/5"
                            data-testid={`create-wishlist-item-${index}`}
                          >
                            {item.image_url ? (
                              <img
                                src={resolveImageUrl(item.image_url)}
                                alt=""
                                className="h-10 w-10 shrink-0 rounded-lg border border-[#1b1033]/10 object-cover dark:border-white/10"
                                data-testid={`create-wishlist-item-photo-${index}`}
                              />
                            ) : (
                              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#1b1033]/[0.04] dark:bg-white/10 text-lg leading-none">
                                {wishlistEmoji(item.icon)}
                              </span>
                            )}
                            <span className="flex-1 min-w-0 truncate font-medium text-[#1b1033] dark:text-[#f4f0ff]">
                              {item.title}
                            </span>
                            <span className="text-sm tabular-nums text-[#4a3d66] dark:text-gray-400">
                              {currencySymbol(item.currency)}
                              {formatMoney(item.target_amount)}
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeWishlistItem(index)}
                              className="rounded-full text-[#4a3d66] hover:text-[#ff4d8d] hover:bg-[#ff4d8d]/[0.06] shrink-0"
                              aria-label={appLang === 'ka' ? 'წაშლა' : 'Remove'}
                              data-testid={`remove-wishlist-item-${index}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )
                      })}
                    </div>
                  )}

                  {/* Inline add form */}
                  <div className="p-4 rounded-xl border border-dashed border-[#1b1033]/15 dark:border-white/10 space-y-3">
                    <Input
                      value={wishlistDraft.title}
                      onChange={(e) =>
                        setWishlistDraft({ ...wishlistDraft, title: e.target.value })
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          addWishlistDraft()
                        }
                      }}
                      placeholder={t.itemTitlePlaceholder}
                      maxLength={140}
                      className="h-11 bg-white dark:bg-white/5 border-[#1b1033]/12 dark:border-white/10 rounded-xl"
                      data-testid="create-wishlist-title-input"
                    />

                    {/* Icon picker */}
                    <div
                      className="grid grid-cols-8 gap-1.5"
                      data-testid="create-wishlist-icon-picker"
                    >
                      {WISHLIST_ICONS.map(({ id, emoji }) => (
                        <button
                          key={id}
                          type="button"
                          onClick={() =>
                            setWishlistDraft({ ...wishlistDraft, icon: id })
                          }
                          aria-label={id}
                          aria-pressed={wishlistDraft.icon === id}
                          className={`flex h-9 items-center justify-center rounded-lg border text-lg leading-none transition-colors ${
                            wishlistDraft.icon === id
                              ? 'border-[#ff4d8d] bg-[#ff4d8d]/10'
                              : 'border-[#1b1033]/10 hover:border-[#1b1033]/25 dark:border-white/10 dark:hover:border-white/25'
                          }`}
                          data-testid={`create-wishlist-icon-${id}`}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>

                    {/* A picture of the gift, optional */}
                    <WishlistImageUpload
                      value={wishlistDraft.image_url}
                      onChange={(url) => setWishlistDraft({ ...wishlistDraft, image_url: url })}
                      appLang={appLang}
                      testId="create-wishlist-image"
                    />

                    {/* Amount + currency + add */}
                    <div className="flex flex-col sm:flex-row gap-2">
                      <div className="flex gap-2 flex-1">
                        <Input
                          type="text"
                          inputMode="decimal"
                          maxLength={9}
                          value={wishlistDraft.target_amount}
                          onChange={(e) => {
                            // type="text" + manual sanitizing: controlled
                            // type="number" inputs drop digits on mobile keyboards.
                            // ',' is the decimal key on ka-GE keypads — map it to '.'.
                            const v = e.target.value
                              .replace(/,/g, '.')
                              .replace(/[^0-9.]/g, '')
                              .replace(/(\..*)\./g, '$1')
                            setWishlistDraft({
                              ...wishlistDraft,
                              target_amount: v,
                            })
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault()
                              addWishlistDraft()
                            }
                          }}
                          placeholder={t.targetAmount}
                          className="h-11 bg-white dark:bg-white/5 border-[#1b1033]/12 dark:border-white/10 rounded-xl flex-1"
                          data-testid="create-wishlist-amount-input"
                        />
                        <Select
                          value={wishlistDraft.currency}
                          onValueChange={(value) =>
                            setWishlistDraft({ ...wishlistDraft, currency: value })
                          }
                        >
                          <SelectTrigger
                            className="w-28 h-11 bg-white dark:bg-white/5 border-[#1b1033]/12 dark:border-white/10 rounded-xl"
                            data-testid="create-wishlist-currency-select"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {GIFT_CURRENCIES.map((cur) => (
                              <SelectItem key={cur} value={cur}>
                                {currencySymbol(cur)} {cur}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        type="button"
                        onClick={addWishlistDraft}
                        variant="outline"
                        className="h-11 rounded-xl sm:w-auto"
                        data-testid="add-wishlist-item-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        {t.addItem}
                      </Button>
                    </div>
                  </div>
                </div>
                )}

                {/* How Storyfy gets paid. There is nothing to choose — an
                    event is free to create and we only ever take a cut of the
                    gifts it actually raises. */}
                <div className="rounded-2xl border border-[#1b1033]/8 dark:border-white/10 bg-celebration-soft/60 dark:bg-white/5 p-4">
                  <Label className="mb-2 flex items-center gap-2 text-sm font-medium text-[#1b1033]/70 dark:text-gray-400">
                    <Percent className="h-4 w-4" />
                    {appLang === 'ka' ? 'ფასი' : 'Pricing'}
                  </Label>
                  <p className="text-sm text-[#4a3d66] dark:text-gray-400">
                    {appLang === 'ka' ? 'ღონისძიების შექმნა უფასოა. ვიღებთ ' : 'Creating an event is free. We keep '}
                    <span className="font-semibold text-[#1b1033] dark:text-[#f4f0ff]">
                      {Math.round((billingConfig?.commission_rate ?? 0.05) * 100)}%
                    </span>
                    {appLang === 'ka'
                      ? '-ს მხოლოდ იმ საჩუქრებიდან, რასაც შეაგროვებ. თუ საჩუქრები არ იქნება, არაფერს იხდი.'
                      : ' of the gift funds you raise — and nothing at all if you raise nothing.'}
                  </p>
                </div>
              </div>
            )}

            {/* Wizard navigation */}
            <div className="flex gap-3 pt-4">
              {step === 0 ? (
                <Link to="/dashboard" className="flex-1">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-12 rounded-full"
                  >
                    {t.cancel}
                  </Button>
                </Link>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBack}
                  className="flex-1 h-12 rounded-full"
                  data-testid="create-event-back"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  {appLang === 'ka' ? 'უკან' : 'Back'}
                </Button>
              )}
              {isLastStep ? (
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 h-12 btn-gradient text-white rounded-full font-semibold"
                  data-testid="create-event-submit"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : isEdit ? (
                    t.save
                  ) : (
                    t.createEvent
                  )}
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleNext}
                  className="flex-1 h-12 btn-gradient text-white rounded-full font-semibold"
                  data-testid="create-event-next"
                >
                  {appLang === 'ka' ? 'შემდეგი' : 'Next'}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </form>
        </div>
      </main>
      {/* Outside the step card: its entrance animation leaves a transform
          behind, which would pin a fixed button to the card, not the screen.
          Below lg the preview sits under the designer; this keeps it one tap away. */}
      {wideStep && (
        <button
          type="button"
          onClick={() => document.getElementById('design-preview')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          className="fixed bottom-5 right-5 z-40 inline-flex h-11 items-center gap-2 rounded-full bg-[#1b1033] px-4 text-sm font-semibold text-white shadow-celebrate-md lg:hidden dark:bg-white dark:text-[#1b1033]"
          data-testid="design-preview-jump"
        >
          <Eye className="h-4 w-4" />
          {appLang === 'ka' ? 'გადახედვა' : 'Preview'}
        </button>
      )}
      <AppFooter maxWidth={pageWidth} />

      {showCelebration && (
        <CelebrationOverlay
          onComplete={() => {
            setShowCelebration(false)
            goToCreatedEvent()
          }}
        />
      )}
    </div>
  )
}

export default CreateEvent
